(() => {
  const roots = document.querySelectorAll("[data-all-six-shop-beta]");

  function escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function formatMoney(amount = 0, currency = "USD") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(Number(amount || 0) / 100);
  }

  roots.forEach((root) => {
    if (root.dataset.initialized === "true") return;
    root.dataset.initialized = "true";

    const apiBase = (root.dataset.apiBase || "https://client.allsixsalon.com").replace(/\/$/, "");
    const fallbackUrl = root.dataset.fallbackUrl || "/collections/all";
    const app = root.querySelector("[data-shop-app]");
    const status = root.querySelector("[data-shop-status]");
    const state = {
      config: null,
      categories: [],
      products: [],
      filter: "all",
      cart: [],
      quote: null,
      detailProductId: "",
      loading: false,
      message: "",
      confirmation: null,
    };

    async function api(path, options = {}) {
      const response = await fetch(`${apiBase}${path}`, {
        ...options,
        headers: {
          accept: "application/json",
          "content-type": "application/json",
          ...(options.headers || {}),
        },
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload.error || payload.message || "Request failed.");
      }

      return payload;
    }

    function setStatus(message, strong = "") {
      if (!status) return;
      status.innerHTML = strong
        ? `<strong>${escapeHtml(strong)}</strong> <span>${escapeHtml(message)}</span>`
        : `<span>${escapeHtml(message)}</span>`;
    }

    function productMatchesFilter(product) {
      if (state.filter === "all") return true;
      const category = state.categories.find((item) => item.slug === state.filter);
      return product.categories?.some((productCategory) => productCategory.id === category?.squareCategoryId);
    }

    function productPrice(product) {
      return product.variations?.[0]?.price?.formatted || "";
    }

    function productStockLabel(product) {
      const variation = product.variations?.find((item) => item.purchasable) || product.variations?.[0];
      const labels = {
        in_stock: "In stock",
        low_stock: "Low stock",
        out_of_stock: "Out of stock",
        unknown: "Stock pending",
      };

      return labels[variation?.inventoryStatus] || "Stock pending";
    }

    function variationOptionLabel(variation) {
      const parts = [variation.title, variation.price?.formatted].filter(Boolean);

      if (!variation.purchasable) {
        parts.push("Unavailable");
      }

      return parts.join(" ");
    }

    function selectedProduct() {
      return state.products.find((product) => product.id === state.detailProductId) || null;
    }

    function cartPayload() {
      return state.cart.map((line) => ({
        productId: line.productId,
        variationId: line.variationId,
        quantity: line.quantity,
      }));
    }

    function cartSubtotalLabel() {
      const subtotal = state.cart.reduce((sum, line) => sum + line.unitAmount * line.quantity, 0);
      const currency = state.cart[0]?.currency || "USD";
      return formatMoney(subtotal, currency);
    }

    function addToCart(productId) {
      const product = state.products.find((item) => item.id === productId);
      const variationSelect = [...root.querySelectorAll("[data-variation]")].find((input) => input.dataset.variation === productId);
      const quantityInput = [...root.querySelectorAll("[data-quantity]")].find((input) => input.dataset.quantity === productId);
      const variationId = variationSelect?.value || product?.defaultVariationId;
      const variation = product?.variations?.find((item) => item.id === variationId);
      const quantity = Math.max(1, Math.min(20, Number.parseInt(quantityInput?.value, 10) || 1));

      if (!product || !variation) return;

      if (!variation.purchasable) {
        state.message = `${product.title} is not currently available for beta checkout.`;
        render();
        return;
      }

      const existing = state.cart.find((line) => line.variationId === variationId);

      if (existing) {
        existing.quantity = Math.min(20, existing.quantity + quantity);
      } else {
        state.cart.push({
          productId: product.id,
          variationId,
          title: product.title,
          variationTitle: variation.title,
          quantity,
          unitAmount: variation.price?.amount || 0,
          currency: variation.price?.currency || "USD",
        });
      }

      state.quote = null;
      state.confirmation = null;
      render();
    }

    function removeFromCart(variationId) {
      state.cart = state.cart.filter((line) => line.variationId !== variationId);
      state.quote = null;
      state.confirmation = null;
      render();
    }

    function renderToolbar() {
      return `
        <div class="as-shop-beta__toolbar" aria-label="Shop categories">
          <button class="as-shop-beta__filter" type="button" data-filter="all" aria-pressed="${state.filter === "all"}">All</button>
          ${state.categories.map((category) => `
            <button class="as-shop-beta__filter" type="button" data-filter="${escapeHtml(category.slug)}" aria-pressed="${state.filter === category.slug}">
              ${escapeHtml(category.title)}
            </button>
          `).join("")}
        </div>
      `;
    }

    function renderDetail() {
      const product = selectedProduct();
      if (!product) return "";

      return `
        <section class="as-shop-beta__notice">
          <strong>${escapeHtml(product.title)}</strong>
          <p>${escapeHtml(product.description || "Provider-recommended retail item.")}</p>
          <p>${escapeHtml((product.categories || []).map((category) => category.title).filter(Boolean).join(" · "))}</p>
        </section>
      `;
    }

    function renderProducts() {
      const products = state.products.filter(productMatchesFilter);

      if (state.message) {
        return `<div class="as-shop-beta__notice">${escapeHtml(state.message)}</div>`;
      }

      if (!products.length) {
        return `
          <div class="as-shop-beta__empty">
            Square retail products are not mapped for this beta page yet.
            <a class="as-shop-beta__link as-shop-beta__link--quiet" href="${escapeHtml(fallbackUrl)}">Open current shop</a>
          </div>
        `;
      }

      return `
        <div class="as-shop-beta__products">
          ${products.map((product) => {
            const defaultVariation = product.variations?.find((variation) => variation.purchasable) || product.variations?.[0];
            return `
            <article class="as-shop-beta__product">
              <div class="as-shop-beta__product-media">
                ${product.images?.[0] ? `<img src="${escapeHtml(product.images[0])}" alt="${escapeHtml(product.title)}" loading="lazy">` : ""}
              </div>
              <div>
                <h3>${escapeHtml(product.title)}</h3>
                <p>${escapeHtml(product.description || "All Six retail selection.")}</p>
                <p class="as-shop-beta__meta">${escapeHtml(productPrice(product))} · ${escapeHtml(productStockLabel(product))}</p>
              </div>
              <div class="as-shop-beta__variant-row">
                <select data-variation="${escapeHtml(product.id)}" aria-label="${escapeHtml(product.title)} variation">
                  ${(product.variations || []).map((variation) => `
                    <option value="${escapeHtml(variation.id)}"${variation.id === defaultVariation?.id ? " selected" : ""}${variation.purchasable ? "" : " disabled"}>${escapeHtml(variationOptionLabel(variation))}</option>
                  `).join("")}
                </select>
                <input data-quantity="${escapeHtml(product.id)}" aria-label="Quantity" type="number" min="1" max="20" value="1">
              </div>
              <button class="as-shop-beta__button" type="button" data-add="${escapeHtml(product.id)}"${product.available ? "" : " disabled"}>Add</button>
              <button class="as-shop-beta__button as-shop-beta__button--quiet" type="button" data-detail="${escapeHtml(product.id)}">Details</button>
            </article>
          `;
          }).join("")}
        </div>
      `;
    }

    function renderCart() {
      const subtotal = state.cart.reduce((sum, line) => sum + line.unitAmount * line.quantity, 0);
      const currency = state.cart[0]?.currency || "USD";

      return `
        <aside class="as-shop-beta__cart" aria-label="Shop cart">
          <p class="as-shop-beta__label">Cart</p>
          <h2>${state.cart.length ? "Review items" : "No items yet"}</h2>
          <div class="as-shop-beta__cart-lines">
            ${state.cart.length ? state.cart.map((line) => `
              <div class="as-shop-beta__cart-line">
                <div>
                  <strong>${escapeHtml(line.title)}</strong>
                  <small>${escapeHtml(line.variationTitle)} · Qty ${line.quantity}</small>
                </div>
                <button class="as-shop-beta__button as-shop-beta__button--quiet" type="button" data-remove="${escapeHtml(line.variationId)}">Remove</button>
              </div>
            `).join("") : '<p class="as-shop-beta__meta">Add a product to start a beta cart.</p>'}
          </div>
          <p><strong>${escapeHtml(formatMoney(subtotal, currency))}</strong></p>
          ${state.quote?.cart?.fulfillment?.message ? `<p class="as-shop-beta__meta">${escapeHtml(state.quote.cart.fulfillment.message)}</p>` : ""}
          ${state.confirmation ? `
            <div class="as-shop-beta__notice">
              <strong>${escapeHtml(state.confirmation.title || "Cart reviewed")}</strong>
              <p>${escapeHtml(state.confirmation.message || "")}</p>
            </div>
          ` : ""}
          <div class="as-shop-beta__cart-actions">
            <button class="as-shop-beta__button" type="button" data-quote ${!state.cart.length || state.loading ? "disabled" : ""}>Quote Cart</button>
            <button class="as-shop-beta__button" type="button" data-order ${!state.cart.length || state.loading ? "disabled" : ""}>${state.config?.shopOrderCreateEnabled ? "Create Order" : "Review Request"}</button>
            <a class="as-shop-beta__link as-shop-beta__link--quiet" href="${escapeHtml(fallbackUrl)}">Open current shop</a>
          </div>
        </aside>
      `;
    }

    function render() {
      if (!app) return;

      if (state.config?.enabled && state.config?.squareConfigured) {
        setStatus("Retail products and inventory are loading from Square.", "Beta ready");
      } else {
        setStatus("Current shop remains the fallback while Square retail mapping is completed.", "Safe mode");
      }

      app.innerHTML = `
        <div class="as-shop-beta__grid">
          <main>
            ${renderToolbar()}
            ${renderDetail()}
            ${renderProducts()}
          </main>
          ${renderCart()}
        </div>
      `;
    }

    async function load() {
      setStatus("Loading shop options...");

      try {
        const [config, categories, products] = await Promise.all([
          api("/api/public/shop/config"),
          api("/api/public/shop/categories"),
          api("/api/public/shop/products"),
        ]);
        state.config = config;
        state.categories = categories.categories || products.categories || [];
        state.products = products.products || [];
        state.message = products.available || state.products.length ? "" : (products.message || products.error || "");
        render();
      } catch (error) {
        state.message = error.message;
        setStatus(error.message, "Shop unavailable");
        render();
      }
    }

    async function quoteCart() {
      state.loading = true;
      render();

      try {
        state.quote = await api("/api/public/shop/cart/quote", {
          method: "POST",
          body: JSON.stringify({ items: cartPayload() }),
        });
        state.message = "";
      } catch (error) {
        state.message = error.message;
      } finally {
        state.loading = false;
        render();
      }
    }

    async function createOrder() {
      state.loading = true;
      render();

      if (state.config?.shopOrderCreateEnabled === false) {
        try {
          state.quote = await api("/api/public/shop/cart/quote", {
            method: "POST",
            body: JSON.stringify({ items: cartPayload() }),
          });
          state.confirmation = {
            title: "Cart ready",
            message: `Your ${cartSubtotalLabel()} cart is ready for All Six follow-up. Use the current shop link when you are ready to place it.`,
          };
          state.message = "";
        } catch (error) {
          state.message = error.message;
        } finally {
          state.loading = false;
          render();
        }
        return;
      }

      try {
        const result = await api("/api/public/shop/orders", {
          method: "POST",
          headers: {
            "idempotency-key": crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
          },
          body: JSON.stringify({ items: cartPayload() }),
        });
        if (!result.created) {
          state.confirmation = {
            title: "Cart ready",
            message: `Your ${cartSubtotalLabel()} cart is ready for All Six follow-up. Use the current shop link when you are ready to place it.`,
          };
          state.message = "";
          render();
          return;
        }
        state.confirmation = {
          title: "Order created",
          message: `Order ${result.order?.id || ""} was created for manual follow-up.`,
          id: result.order?.id || "",
        };
        state.quote = result;
      } catch (error) {
        state.message = error.message;
      } finally {
        state.loading = false;
        render();
      }
    }

    root.addEventListener("click", (event) => {
      const filter = event.target.closest("[data-filter]");
      const add = event.target.closest("[data-add]");
      const remove = event.target.closest("[data-remove]");
      const detail = event.target.closest("[data-detail]");

      if (filter) {
        state.filter = filter.dataset.filter;
        render();
      }

      if (add) {
        addToCart(add.dataset.add);
      }

      if (remove) {
        removeFromCart(remove.dataset.remove);
      }

      if (detail) {
        state.detailProductId = state.detailProductId === detail.dataset.detail ? "" : detail.dataset.detail;
        render();
      }

      if (event.target.closest("[data-quote]")) {
        quoteCart();
      }

      if (event.target.closest("[data-order]")) {
        createOrder();
      }
    });

    load();
  });
})();
